import type { DashGraphics, DashTextureScene } from './projectile-art';

/**
 * Procedural neon starfield textures.
 *
 * The previous background used flat filled circles, which read as cheap opaque
 * dots. A star should be a very small, very bright core wrapped in a soft glow
 * that falls off smoothly to nothing - drawn once into a reusable texture and
 * blended additively so it never looks like a pasted disc.
 *
 * ## Exactly three textures
 *
 * The textures are painted WHITE and coloured per sprite with `setTint`.
 * Generating one texture per colour instead would multiply the number of
 * textures in the display list, and every texture switch breaks Phaser's
 * batching - which measurably halved the frame rate when the starfield was
 * first built this way. A tint is a vertex attribute, so tinted sprites of the
 * same texture still batch into a single draw call.
 *
 * Stars themselves are pooled sprites that recycle when they leave the arena,
 * so no object is created or destroyed during a frame.
 */

/** Star tints. White-hot, cyan and a rare violet, matching the art direction. */
export const STAR_TINTS = [0xffffff, 0xcfe6ff, 0x8fd8ff, 0xb79bff] as const;

export type StarKind = 'point' | 'cross' | 'streak';

const GLOW_SIZE = 32;
const CROSS_SIZE = 48;

export const STAR_TEXTURE_KEYS: Readonly<Record<StarKind, string>> = Object.freeze({
  point: 'star-point',
  cross: 'star-cross',
  streak: 'star-streak',
});

/**
 * Radial falloff of the glow.
 *
 * `d` is the normalised distance from the centre. A steep inner curve keeps the
 * core tiny and intense; the long tail is what makes it read as light rather
 * than a disc. Painted as concentric rings, which is smooth enough at this size
 * and costs nothing after the first generation.
 */
function glowAlpha(d: number): number {
  if (d >= 1) {
    return 0;
  }
  const falloff = (1 - d) ** 3.2;
  // A small, very bright core on top of the wide soft halo.
  const core = d < 0.16 ? (1 - d / 0.16) ** 1.5 : 0;
  return Math.min(1, falloff * 0.55 + core * 0.85);
}

function paintGlow(graphics: DashGraphics, cx: number, cy: number, radius: number, color: number) {
  // Outside in, so brighter rings land on top.
  const steps = Math.ceil(radius);
  for (let i = steps; i >= 1; i -= 1) {
    const d = i / steps;
    const alpha = glowAlpha(d);
    if (alpha <= 0.004) {
      continue;
    }
    graphics.fillStyle(color, alpha);
    graphics.fillCircle(cx, cy, (radius * i) / steps);
  }
}

/** Soft round star: tiny bright core plus a wide neon halo. White; tint it. */
export function ensureStarGlowTexture(scene: DashTextureScene): string {
  const key = STAR_TEXTURE_KEYS.point;
  if (scene.textures.exists(key)) {
    return key;
  }
  const graphics = scene.make.graphics({ x: 0, y: 0 }, false);
  paintGlow(graphics, GLOW_SIZE / 2, GLOW_SIZE / 2, GLOW_SIZE / 2, 0xffffff);
  graphics.generateTexture(key, GLOW_SIZE, GLOW_SIZE);
  graphics.destroy();
  return key;
}

/**
 * Large star with a cross-shaped flare.
 *
 * Used sparingly - a handful per screen - as the "big" stars. The spikes are
 * tapered bars, not lines, so they fade instead of ending abruptly.
 */
export function ensureStarCrossTexture(scene: DashTextureScene): string {
  const key = STAR_TEXTURE_KEYS.cross;
  if (scene.textures.exists(key)) {
    return key;
  }
  const size = CROSS_SIZE;
  const centre = size / 2;
  const color = 0xffffff;
  const graphics = scene.make.graphics({ x: 0, y: 0 }, false);

  // Diffraction spikes first, so the core glow sits on top of them.
  const spikeLength = centre;
  for (let i = 1; i < spikeLength; i += 1) {
    const t = i / spikeLength;
    const alpha = (1 - t) ** 2.6 * 0.5;
    if (alpha <= 0.004) {
      continue;
    }
    const halfThickness = Math.max(0.5, 1.6 * (1 - t));
    graphics.fillStyle(color, alpha);
    // Horizontal arms.
    graphics.fillRect(centre + i, centre - halfThickness, 1, halfThickness * 2);
    graphics.fillRect(centre - i, centre - halfThickness, 1, halfThickness * 2);
    // Vertical arms.
    graphics.fillRect(centre - halfThickness, centre + i, halfThickness * 2, 1);
    graphics.fillRect(centre - halfThickness, centre - i, halfThickness * 2, 1);
  }
  paintGlow(graphics, centre, centre, centre * 0.42, color);

  graphics.generateTexture(key, size, size);
  graphics.destroy();
  return key;
}

/**
 * Fast elongated streak for the foreground layer.
 *
 * Conveys speed without brightening the background: thin, short, and fading at
 * both ends.
 */
export function ensureStarStreakTexture(scene: DashTextureScene): string {
  const key = STAR_TEXTURE_KEYS.streak;
  if (scene.textures.exists(key)) {
    return key;
  }
  const length = 26;
  const height = 6;
  const color = 0xffffff;
  const graphics = scene.make.graphics({ x: 0, y: 0 }, false);
  for (let i = 0; i < length; i += 1) {
    const t = i / (length - 1);
    // Brightest near the leading end, tapering away behind.
    const shape = Math.sin(Math.PI * t) ** 1.4;
    const half = 1.5 * shape;
    if (half <= 0.05) {
      continue;
    }
    graphics.fillStyle(color, 0.1 + 0.55 * shape);
    graphics.fillRect(i, height / 2 - half, 1, half * 2);
  }
  graphics.generateTexture(key, length, height);
  graphics.destroy();
  return key;
}

export interface StarLayerConfig {
  readonly name: string;
  /** How many stars this layer keeps alive. Pooled, never re-allocated. */
  readonly count: number;
  /** Downward speed in arena units per second. */
  readonly speed: number;
  readonly minScale: number;
  readonly maxScale: number;
  readonly minAlpha: number;
  readonly maxAlpha: number;
  /** How many of this layer's stars get the cross flare (0 = none). */
  readonly crossCount: number;
  /** Foreground layers use elongated streaks instead of round glows. */
  readonly streak: boolean;
  /** Twinkle amplitude, 0 disables it. Kept gentle - never a hard blink. */
  readonly twinkle: number;
  readonly depth: number;
}

/**
 * Three depth layers: distant/slow/dim, middle, and a fast bright foreground.
 * The counts are modest on purpose - the arena must stay predominantly black.
 */
export const STAR_LAYERS: readonly StarLayerConfig[] = Object.freeze([
  {
    name: 'far',
    count: 120,
    speed: 9,
    minScale: 0.16,
    maxScale: 0.3,
    minAlpha: 0.25,
    maxAlpha: 0.5,
    crossCount: 0,
    streak: false,
    twinkle: 0.1,
    depth: 0,
  },
  {
    name: 'mid',
    count: 55,
    speed: 26,
    minScale: 0.28,
    maxScale: 0.5,
    minAlpha: 0.4,
    maxAlpha: 0.75,
    crossCount: 4,
    streak: false,
    twinkle: 0.16,
    depth: 1,
  },
  {
    name: 'near',
    count: 26,
    speed: 74,
    minScale: 0.5,
    maxScale: 0.85,
    minAlpha: 0.5,
    maxAlpha: 0.9,
    crossCount: 3,
    streak: true,
    twinkle: 0.2,
    depth: 2,
  },
]);

export const STAR_LAYER_COUNT = STAR_LAYERS.length;

/** Total pooled sprites: fixed for the scene's lifetime. */
export const TOTAL_STAR_COUNT = STAR_LAYERS.reduce((sum, layer) => sum + layer.count, 0);
